"use client"

import React, { useState, useEffect, useCallback } from "react"
import { X, Menu, ArrowRight } from "lucide-react"
import Link from "next/link"
import { usePathname, useRouter } from "next/navigation"

import { Logo } from "@/components/molecules/Logo"
import { HeaderTranslations } from "@/hooks/useTranslation"
import { useUserStore } from "@/stores/user"

const Navbar = ({ nav, user }: { nav: HeaderTranslations, user?: any | null }) => {
  const currentPath = usePathname()
  const router = useRouter()
  const { clearUser } = useUserStore()
  const [menuOpen, setMenuOpen] = useState(false)

  const MenuLinks = [
    { id: 1, name: nav.nav.home, link: "/" },
    { id: 2, name: nav.nav.shop, link: "/shop" },
    { id: 3, name: nav.nav.jobs, link: "/jobs" },
    { id: 4, name: nav.nav.property, link: "/properties" },
    { id: 5, name: nav.nav.consultants, link: "/consultants" },
    { id: 6, name: nav.nav.news, link: "/stories" },
  ]

  const toggleMenu = useCallback(() => setMenuOpen((prev) => !prev), [])
  const closeMenu = useCallback(() => setMenuOpen(false), [])

  // Lock background scroll when open
  useEffect(() => {
    if (window.innerWidth < 1280) { // xl breakpoint
      document.body.style.overflow = menuOpen ? "hidden" : ""
    }
    return () => {
      document.body.style.overflow = ""
    }
  }, [menuOpen])

  // Close on route change
  useEffect(() => {
    closeMenu()
  }, [currentPath, closeMenu])

  // Escape key closes menu
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeMenu()
    }
    window.addEventListener("keydown", handleEsc)
    return () => window.removeEventListener("keydown", handleEsc)
  }, [closeMenu])

  return (
    <nav className="w-full p-4 relative">
      {/* Mobile Toggle */}
      <div className="flex items-center justify-between xl:hidden">
        <button
          className="text-2xl focus:outline-none dark:text-white"
          onClick={toggleMenu}
          aria-label="Toggle menu"
          aria-expanded={menuOpen}
        >
          {!menuOpen && <Menu />}
        </button>
      </div>

      {/* Modal-like Overlay */}
      {menuOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/20 backdrop-blur-[2px] transition-opacity duration-300 xl:hidden"
          onClick={closeMenu}
          role="presentation"
        />
      )}

      {/* Sidebar Drawer */}
      <aside
        className={`fixed xl:hidden inset-y-0 left-0 z-50 w-80 bg-white p-6 shadow-lg transition-transform ease-in-out duration-300 dark:bg-gray-950 transform ${menuOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        role="dialog"
        aria-modal="true"
      >
        <div className="mb-8 flex items-center justify-between">
          <Link href="/" aria-label="Home">
            <Logo />
          </Link>
          <button
            className="text-2xl focus:outline-none dark:text-white"
            onClick={closeMenu}
            aria-label="Close menu"
          >
            {menuOpen && <X />}
          </button>
        </div>

        <ul className="space-y-4 text-left">
          {MenuLinks.map((item) => (
            <li key={item.id} className="border-b">
              <Link
                href={item.link}
                className="block w-full rounded-md px-4 py-2 font-semibold text-gray-700 hover:bg-gray-100 dark:text-white"
                onClick={closeMenu}
              >
                {item.name}
              </Link>
            </li>
          ))}

          {!user ? (
            <>
              <li className="border-b">
                <Link href="/auth/signup" onClick={closeMenu}>
                  <span className="block w-full rounded-md px-4 py-2 font-semibold text-gray-700 hover:bg-gray-100 dark:text-white">
                    {nav.signup}
                  </span>
                </Link>
              </li>
              <li className="border-b">
                <Link href="/auth/login" onClick={closeMenu}>
                  <span className="block w-full rounded-md px-4 py-2 font-semibold text-gray-700 hover:bg-gray-100 dark:text-white">
                    {nav.login}
                  </span>
                </Link>
              </li>
            </>
          ) : (
            <li className="border-b">
              <button
                type="button"
                className="block w-full rounded-md px-4 py-2 font-semibold text-gray-700 hover:bg-gray-100 dark:text-white text-left"
                onClick={async () => {
                  await fetch("/api/auth/signout", { method: "POST" })
                  closeMenu()
                  clearUser()
                  router.refresh() // Force server components to re-render with cleared cookies
                  router.push("/")
                }}
              >
                {nav.logout}
              </button>
            </li>
          )}
        </ul>

        <Link href="/dashboard" onClick={closeMenu}>
          <div className="w-full p-5 flex justify-between items-center hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md">
            <span className="font-semibold text-gray-700 dark:text-white">
              {nav.switch}
            </span>
            <ArrowRight className="animate-ping" />
          </div>
        </Link>
      </aside>

      {/* Desktop Nav */}
      <ul className="hidden xl:flex justify-between gap-x-10">
        {MenuLinks.map((item) => (
          <li key={item.id}>
            <Link
              href={item.link}
              className={`inline-block cursor-pointer font-serif text-lg font-semibold text-gray-500 hover:text-black dark:hover:text-white duration-200 ${currentPath === item.link ? "active-link" : ""
                }`}
            >
              {item.name}
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  )
}

export default Navbar
